/**
 * THE PLAN CARD'S CASHFLOW TILES ADD UP TO ITS NET (Support at home review, 18 Oct 2026).
 *
 * The card showed six tiles — Age Pension, investment income, living costs, the contribution, tax and
 * the net — and they added to $22,846 against a stated $2,277. Two things were missing:
 *
 *  - lines with no tile: rental expenses and loan repayments (and capital expenses, housing costs and
 *    the like when a file has them), which the engine's own breakdown lists;
 *  - the in-year movement. The net is the money the year actually paid, and the Age Pension in it is
 *    re-assessed every fortnight, while the tile shows the rate the year opened on.
 *
 * `cashflowNotOnTiles` is what the tiles leave out; `otherCashflowLines` itemises it from the engine's
 * breakdown, and names whatever is left as the change through the year, so the lines always close.
 */

export interface SahSnapshotLike {
    pension?: number;
    agePension?: number;
    abpIncome?: number;
    rentalIncome?: number;
    otherInvestmentIncome?: number;
    livingExpenses?: number;
    totalSupportAtHomeCosts?: number;
    taxPayable?: number;
    cashflow?: number;
    cashFlowBreakdown?: unknown;
    [key: string]: unknown;
}

/** Net cashflow less what the card's tiles show. */
export function cashflowNotOnTiles(snapshot: SahSnapshotLike | null | undefined): number {
    if (!snapshot) return 0;
    const tiles =
        (snapshot.pension || snapshot.agePension || 0) +
        (snapshot.abpIncome || 0) + (snapshot.rentalIncome || 0) + (snapshot.otherInvestmentIncome || 0) -
        (snapshot.livingExpenses || 0) -
        (snapshot.totalSupportAtHomeCosts || 0) -
        (snapshot.taxPayable ?? 0);
    return (snapshot.cashflow || 0) - tiles;
}

/** Lines the tiles already carry: income, the Age Pension, living costs, the care cost and tax. */
const COVERED_BY_TILES = /^(Interest|Dividends|Rent \(property\)|Trust & Company|Other Investment|Investment Income|ABP Income|Age Pension|Less: Living Expenses|Less: Aged Care Costs|Less: .*Tax$|Add: .*Tax Refund$)/;

export function otherCashflowLines(breakdown: unknown, other: number): Array<{ label: string; value: number }> {
    const rows: Array<{ label: string; value: number }> = [];
    if (Array.isArray(breakdown)) {
        for (const line of breakdown as Array<{ label?: string; value?: unknown; note?: boolean }>) {
            const value = Number(line?.value);
            if (line?.note || !line?.label || !Number.isFinite(value) || Math.abs(value) < 1) continue;
            if (COVERED_BY_TILES.test(line.label)) continue;
            rows.push({ label: line.label.replace(/^(Less|Add): /, ''), value });
        }
    }
    const listed = rows.reduce((t, r) => t + r.value, 0);
    const movement = other - listed;
    if (Math.abs(movement) >= 1) rows.push({ label: 'Change through the year (what was paid, against the opening rates above)', value: movement });
    return rows;
}
